/**
 * Shared module-level cache for submission image/edit config.
 * Persists across component mount/unmount in the same session.
 * Used by both SubmissionPrintPreview (single-doc) and bulk export.
 */

export interface SubmissionEditState {
  imgSizes: Record<string, number>   // image URL → size % (10–100)
  imgPaired: Record<string, boolean> // image URL → paired with next image
  headingEdit: string
  toolEdits: Record<number, string>
  actEdits: Record<number, string>
  obsEdits: Record<number, string>
  lang: string
}

const cache = new Map<string, SubmissionEditState>()

export const submissionEditState = {
  get(id: string): SubmissionEditState | undefined {
    return cache.get(id)
  },

  set(id: string, state: SubmissionEditState) {
    cache.set(id, state)
  },

  /** Merge imageSizeFactors (0–1) from all listed submission IDs */
  getMergedSizeFactors(ids: string[]): Record<string, number> {
    const result: Record<string, number> = {}
    for (const id of ids) {
      const s = cache.get(id)
      if (!s) continue
      for (const [url, size] of Object.entries(s.imgSizes)) {
        result[url] = size / 100
      }
    }
    return result
  },

  /** Merge imgPaired map from all listed submission IDs */
  getMergedPaired(ids: string[]): Record<string, boolean> {
    const result: Record<string, boolean> = {}
    for (const id of ids) {
      const s = cache.get(id)
      if (!s) continue
      Object.assign(result, s.imgPaired)
    }
    return result
  },
}
